/**
 * 전시관 뷰(C-02 · FS-03-01) 의 순수 매핑 — three 없이 시험한다.
 *  - 받침대(env · surface "pedestal" · owner = 작품 id) 위치 → 미니맵 · 가까운 받침대 · 이동 범위
 *  - 전시관 카메라(고정 3/4 시점 — 목표 점의 남쪽(−z · 입구 쪽) 위에서 북쪽(+z)을 본다)
 *  - 정지 작품 인스턴싱 계획: 같은 GLB 의 같은 지오메트리 = InstancedMesh 하나(작품 × 노드가 인스턴스)
 *  - 인스턴스 색: 작품의 Campus 색(가장 가까운 조상 인스턴스 색) → 없으면 재질 원래 색
 */
import { planColors } from "./appearance";
import type { NodeLike } from "./appearance";
import type { CourseBody, V3 } from "./courseMap";

export interface PedestalInfo {
  owner: string;
  index: number;
  x: number;
  z: number;
}

/** env 몸체 중 surface "pedestal" → 받침대(프레임 자세의 x·z) */
export function pedestalsOf(
  bodies: readonly CourseBody[],
  transforms: Float32Array,
): PedestalInfo[] {
  const out: PedestalInfo[] = [];
  for (const b of bodies) {
    if (b.kind !== "env" || b.surface !== "pedestal") continue;
    out.push({
      owner: b.owner,
      index: b.index,
      x: transforms[b.index * 7] ?? 0,
      z: transforms[b.index * 7 + 2] ?? 0,
    });
  }
  return out;
}

/** maxDist 안에서 가장 가까운 받침대(없으면 null). 같은 거리면 앞의 것 */
export function nearestPedestal(
  pedestals: readonly PedestalInfo[],
  p: { x: number; z: number },
  maxDist: number,
): PedestalInfo | null {
  let best: PedestalInfo | null = null;
  let bestD = maxDist;
  for (const q of pedestals) {
    const d = Math.hypot(q.x - p.x, q.z - p.z);
    if (d <= bestD && (!best || d < bestD)) {
      best = q;
      bestD = d;
    }
  }
  return best;
}

export interface GalleryBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/** 받침대를 감싸는 범위 + 여유(pad m) — 미니맵 · 이동 제한. 받침대가 없으면 원점 둘레 */
export function galleryBounds(pedestals: readonly PedestalInfo[], pad = 1): GalleryBounds {
  if (!pedestals.length) return { minX: -pad, maxX: pad, minZ: -pad, maxZ: pad };
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of pedestals) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minZ = Math.min(minZ, p.z);
    maxZ = Math.max(maxZ, p.z);
  }
  return { minX: minX - pad, maxX: maxX + pad, minZ: minZ - pad, maxZ: maxZ + pad };
}

/** 점을 범위 안으로 */
export function clampToBounds(p: { x: number; z: number }, b: GalleryBounds) {
  return {
    x: Math.min(b.maxX, Math.max(b.minX, p.x)),
    z: Math.min(b.maxZ, Math.max(b.minZ, p.z)),
  };
}

export interface GalleryCamera {
  /** 목표 점 뒤(남쪽 −z)로 m */
  back: number;
  /** 높이 m */
  up: number;
  /** 바라보는 점 높이 m */
  lookUp: number;
  /** 바라보는 점을 선 자리보다 앞(북쪽 +z)으로 m — 다가간 받침대가 화면 가운데 */
  ahead?: number;
  stiffness: number;
}

export const DEFAULT_GALLERY_CAMERA: GalleryCamera = {
  // SPIKE 작품은 20~40cm — 받침대 앞에 선 사람 눈높이 조금 위에서 내려다본다
  back: 1.7,
  up: 1.25,
  lookUp: 0.3,
  ahead: 0.45,
  stiffness: 4,
};

/**
 * 전시관 카메라 목표 — 입구 쪽(남쪽 −z) 위에서 북쪽(+z)을 본다. 작품은 입구(−z)를 보고 선다.
 * `back` 을 주면(첫 화면 맞춤 — {@link fitGalleryView}) 그만큼 뒤로 물러서고, 높이도 같은 비율로 올려 내려다보는 각을 지킨다.
 * `heading`(rad · 0 = +z · +x 쪽이 +)을 주면 그 방향을 보도록 돈다 — 룸 밀어내기의 내 기계 뒤 시점(없으면 0 = 북쪽).
 * `lookUp`(m)을 주면 바라보는 점 높이를 바꾼다 — 룸에서 가까이 따라갈 때 기계 높이(링 위 포함)를 보게(없으면 cam.lookUp).
 */
export function galleryCameraTarget(
  p: { x: number; z: number },
  cam = DEFAULT_GALLERY_CAMERA,
  back = cam.back,
  heading = 0,
  lookUp = cam.lookUp,
) {
  const k = back / cam.back;
  const fx = Math.sin(heading);
  const fz = Math.cos(heading);
  const ahead = cam.ahead ?? 0;
  const eye: V3 = [p.x - fx * back, cam.up * k, p.z - fz * back];
  const look: V3 = [p.x + fx * ahead, lookUp, p.z + fz * ahead];
  return { eye, look };
}

export interface GalleryFit {
  /** 선 자리(바닥 x·z) */
  x: number;
  z: number;
  /** 카메라가 선 자리 뒤로 물러설 거리(m) — 기본보다 작아지지 않는다 */
  back: number;
  /** 첫 화면에 다 들어오는 받침대(owner) */
  owners: string[];
}

/**
 * 첫 화면(S4 실측 보완 — 작품이 적으면 받침대 사이 빈 곳을 보고 작품이 화면 가장자리에서 잘렸다):
 * 앞줄(입구 쪽 · z 가장 작은 줄)의 받침대를 채운 순서(index)대로, 카메라를 maxBack 까지 물려서 화면 폭에 다 들어오는 만큼
 * 골라 그 가운데 앞에 선다. `aspect` = 캔버스 너비/높이, `fovDeg` = 세로 화각(뷰 50°), `standBack` = 받침대 줄에서 선 자리까지(m).
 */
export function fitGalleryView(
  pedestals: readonly PedestalInfo[],
  aspect: number,
  opts: {
    fovDeg?: number;
    standBack?: number;
    margin?: number;
    maxBack?: number;
    cam?: GalleryCamera;
  } = {},
): GalleryFit | null {
  if (!pedestals.length) return null;
  const cam = opts.cam ?? DEFAULT_GALLERY_CAMERA;
  const fov = ((opts.fovDeg ?? 50) * Math.PI) / 180;
  const standBack = opts.standBack ?? 0.9;
  const margin = opts.margin ?? 0.55;
  const maxBack = opts.maxBack ?? 4.2;
  const tanH = Math.tan(fov / 2) * Math.max(0.3, aspect);
  const frontZ = Math.min(...pedestals.map((p) => p.z));
  const row = pedestals.filter((p) => p.z - frontZ < 0.3).sort((a, b) => a.index - b.index);
  const need = (xs: number[]) => {
    const half = (Math.max(...xs) - Math.min(...xs)) / 2 + margin;
    return half / tanH - standBack; // 선 자리에서 뒤로
  };
  let take = 1;
  for (let k = 2; k <= row.length; k++) {
    if (need(row.slice(0, k).map((p) => p.x)) > maxBack) break;
    take = k;
  }
  const group = row.slice(0, take);
  const xs = group.map((p) => p.x);
  return {
    x: (Math.max(...xs) + Math.min(...xs)) / 2,
    z: frontZ - standBack,
    back: Math.max(cam.back, Math.min(maxBack, need(xs))),
    owners: group.map((p) => p.owner),
  };
}

// ── 정지 작품 인스턴싱 ─────────────────────────────────────────────────────

/** GLB 주소별 작품(주소가 없으면 자리표시 목록) */
export function groupExhibitsByModel<E extends { owner: string; glbUrl: string | null }>(
  exhibits: readonly E[],
): { byUrl: Map<string, E[]>; placeholders: E[] } {
  const byUrl = new Map<string, E[]>();
  const placeholders: E[] = [];
  for (const e of exhibits) {
    if (!e.glbUrl) {
      placeholders.push(e);
      continue;
    }
    const list = byUrl.get(e.glbUrl);
    if (list) list.push(e);
    else byUrl.set(e.glbUrl, [e]);
  }
  return { byUrl, placeholders };
}

export interface InstanceEntry {
  owner: string;
  /** 모형 안 메시 번호(순회 순서) */
  mesh: number;
}

export interface ExhibitInstanceGroup {
  /** 지오메트리 키(같은 GLB 안) */
  geometry: string;
  /** 대표 메시(재질 원본) */
  first: number;
  entries: InstanceEntry[];
}

/**
 * 한 GLB 의 메시 목록(각 메시의 지오메트리 키) × 작품들 → InstancedMesh 묶음.
 * 같은 지오메트리를 쓰는 노드(같은 부품이 여러 번)는 한 묶음 — 인스턴스 = 작품 × 노드.
 */
export function planExhibitInstances(
  meshGeometries: readonly string[],
  owners: readonly string[],
): ExhibitInstanceGroup[] {
  const groups = new Map<string, ExhibitInstanceGroup>();
  meshGeometries.forEach((g, mesh) => {
    let grp = groups.get(g);
    if (!grp) {
      grp = { geometry: g, first: mesh, entries: [] };
      groups.set(g, grp);
    }
  });
  for (const owner of owners)
    meshGeometries.forEach((g, mesh) => groups.get(g)!.entries.push({ owner, mesh }));
  return [...groups.values()];
}

/**
 * 작품 하나의 메시별 최종 색 — Campus 색(가장 가까운 조상 인스턴스 노드의 색)이 있으면 그 색,
 * 없으면 재질 원래 색(original). 인스턴스 재질 색은 흰색이고 instanceColor 가 최종 색이다.
 * `paintable` 이 false 인 메시(투명 · 금속 재질 — FIELD-DEC-005 #4 `isPaintable`)는 색이 있어도 원래 색이다.
 */
export function meshColors<N extends NodeLike>(
  root: N,
  meshes: readonly N[],
  colors: ReadonlyMap<string, string>,
  original: (mesh: N, i: number) => string,
  paintable: (mesh: N, i: number) => boolean = () => true,
): string[] {
  const plan = colors.size ? planColors(root, colors) : new Map<N, string>();
  return meshes.map((m, i) => {
    const c = plan.get(m);
    return c && paintable(m, i) ? c : original(m, i);
  });
}

/** 두 자세(7 수)가 같은가 — 정지 작품의 인스턴스 행렬을 다시 계산할지 */
export function samePose(a: ArrayLike<number> | null, tf: Float32Array, index: number): boolean {
  if (!a) return false;
  for (let k = 0; k < 7; k++) if (a[k] !== tf[index * 7 + k]) return false;
  return true;
}
